import React from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { FieldLabel, List, ListItem } from 't3-widgets'

import { DeviceTypeHandler } from '../../../../../../common/components/device-type-handler/device-type-handler'
import { VALIDATORS_CONFIG } from '../../../../../../common/constants'
import { loadingProcess, getValidator, getYearTo } from '../../../common/operations'

import styles from '../pages.module.css'

const ConfirmationList = ({ errorMessage, intl }) => {
    const fetchProcessSuccess = useSelector(state => state.fetchProcessSuccess.fetchProcessSuccess)
    const providerNo = useSelector(state => state.providerNo.providerNo)
    const customerNumber = useSelector(state => state.customerNumber.customerNumber)
    const contractNumber = useSelector(state => state.contractNumber.contractNumber)
    const choosedPropertyType = useSelector(state => state.choosedPropertyType.choosedPropertyType)
    const licensePlate = useSelector(state => state.licensePlate.licensePlate)
    const wfSubscription = useSelector(state => state.wfSubscription.wfSubscription)
    const deviceActivationKey = useSelector(state => state.deviceActivationKey.deviceActivationKey)
    const rmaDeviceSerialNumber = useSelector(state => state.rmaDeviceSerialNumber.rmaDeviceSerialNumber)
    const deviceSerialNumber = useSelector(state => state.deviceSerialNumber.deviceSerialNumber)
    const generationRequired = useSelector(state => state.generationRequired.generationRequired)
    const generationNumber = useSelector(state => state.generationSelected.generationSelected)
    const genObj = useSelector(state => state.generationObject.generationObject)
    const tankSize = useSelector(state => state.tankSize.tankSize)

    const generationName =
        genObj.brand +
        ' ' +
        genObj.model +
        ', ' +
        genObj.yearFrom +
        ' - ' +
        getYearTo(genObj.yearTo, intl) +
        (genObj.isFuelTypeShow ? ', ' + intl.translate(genObj.fuelType) : '')

    const isLink340Device = () => {
        const conf = VALIDATORS_CONFIG.step3

        return getValidator(
            conf.link340.enabled,
            conf.link340.prefix.includes(deviceSerialNumber.substring(0, 2)),
            false
        )
    }

    const getSummary = parity => {
        const typePrefix =
            choosedPropertyType === 'MACHINERY' || choosedPropertyType === 'EQUIPMENT'
                ? 'assettype_asset_type'
                : 'vehicletype_vehicle_type'

        const summary = [
            { label: intl.translate('ap_step1_customernumber_label'), name: customerNumber },
            { label: intl.translate('ap_step2_deviceserial_label'), name: deviceSerialNumber },
            { label: intl.translate('ap_step3_contractnumber_label'), name: providerNo + '-' + contractNumber },
            {
                isVisible: rmaDeviceSerialNumber.length > 0,
                label: intl.translate('ap_step3_rma_label'),
                name: rmaDeviceSerialNumber
            },
            { label: intl.translate('ap_step3_tariffs_label'), name: wfSubscription },
            {
                isVisible: generationRequired && generationNumber !== 0,
                label: intl.translate('ap_step4_generation_label'),
                name: generationName.replace('null', intl.translate('ap_step3_generation_today'))
            },
            {
                isVisible: deviceActivationKey.length > 0,
                label: intl.translate('ap_step2_dak'),
                name: deviceActivationKey
            },
            {
                isVisible: choosedPropertyType.length > 0,
                label: intl.translate(isLink340Device() ? 'ap_step3_assettype_label' : 'ap_step3_vehicletype_label'),
                name: intl.translate(`ap_forms_${typePrefix}_${choosedPropertyType.toLowerCase()}`)
            },
            {
                isVisible: licensePlate.length > 0,
                label: intl.translate('ap_step3_licenseplate_label'),
                name: licensePlate
            },
            {
                isVisible: tankSize.length > 0,
                label: intl.translate('atl_generic-tank-size-label'),
                name: tankSize
            }
        ]

        const fullSummary = []
        const even = []
        const odd = []

        summary.forEach(s => {
            const isVisible = typeof s.isVisible === 'undefined' ? true : s.isVisible

            if (isVisible) {
                const element = {
                    id: fullSummary.length,
                    label: s.label,
                    name: s.name
                }

                if (fullSummary.length % 2 === 1) {
                    even.push(element)
                } else {
                    odd.push(element)
                }
                fullSummary.push(element)
            }
        })

        if (parity === 'even') {
            return even
        } else if (parity === 'odd') {
            return odd
        } else {
            return fullSummary
        }
    }

    const desktopDeviceComponent = (
        <div className={styles['list-container']}>
            <div className={styles['list-column']}>
                <List maxItems={70}>
                    {getSummary('odd').map((item, index) => (
                        <FieldLabel key={index} label={item.label}>
                            <ListItem className={styles['summary-list-item']} key={item.id}>
                                {item.name}
                            </ListItem>
                        </FieldLabel>
                    ))}
                </List>
            </div>
            <div className={styles['list-column']}>
                <List maxItems={70}>
                    {getSummary('even').map((item, index) => (
                        <FieldLabel key={index} label={item.label}>
                            <ListItem className={styles['summary-list-item']} key={item.id}>
                                {item.name}
                            </ListItem>
                        </FieldLabel>
                    ))}
                </List>
            </div>
        </div>
    )

    const mobileDeviceComponent = (
        <div className={styles['list-container']}>
            <div className={styles['list-column']}>
                <List maxItems={70}>
                    {getSummary('all').map((item, index) => (
                        <FieldLabel key={index} label={item.label}>
                            <ListItem className={styles['summary-list-item']} key={item.id}>
                                {item.name}
                            </ListItem>
                        </FieldLabel>
                    ))}
                </List>
            </div>
        </div>
    )

    return (
        <div className={styles['content-wrapper']}>
            <div>
                <div className={styles['page-description']}>
                    <p>{intl.translate('ap_step4_para')}</p>
                </div>
                {loadingProcess(fetchProcessSuccess)}
                <DeviceTypeHandler {...{ desktopDeviceComponent, mobileDeviceComponent }} />
                <div className={styles['error-message']}>{errorMessage}</div>
            </div>
        </div>
    )
}

ConfirmationList.propTypes = {
    errorMessage: PropTypes.string,
    intl: PropTypes.objectOf(PropTypes.func)
}

export { ConfirmationList }
